import {
  avatarGroupClasses,
  AvatarOwnProps,
  type AvatarGroupProps,
  type AvatarProps,
} from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import { ColorSchema } from '../../palette';

const COLORS = [
  'white',
  'default',
  'primary',
  'success',
  'warning',
  'error',
  'purple',
] as const;

const SIZES: AvatarOwnProps['size'][] = [
  12, 16, 22, 24, 32, 40, 48, 56, 64, 72, 80,
];

declare module '@mui/material/Avatar' {
  interface AvatarPropsVariantOverrides {
    // square: false;
  }

  interface AvatarOwnProps {
    size?: 80 | 72 | 64 | 56 | 48 | 40 | 32 | 24 | 22 | 16 | 12;
    color?: ColorSchema | 'white';
  }
}

// NEW VARIANT
declare module '@mui/material/AvatarGroup' {
  interface AvatarGroupPropsVariantOverrides {
    compact: true;
  }
}

function getBorderRadiusBySizeAndVariant(
  theme: Theme,
  variant: AvatarProps['variant'],
  size: number
) {
  if (variant === 'rounded')
    return size > 24
      ? theme.spacing(1)
      : size === 24
      ? theme.spacing(0.5)
      : theme.spacing(0.25);
  else if (variant === 'square') return 0;
  else return '50%';
}

function getFontSizeBySize(theme: Theme, size: number) {
  if (size < 22) return theme.typography.body5.fontSize;
  if (size < 32) return theme.typography.body4.fontSize;
  if (size < 48) return theme.typography.body3.fontSize;
  if (size < 72) return theme.typography.body2.fontSize;
  return theme.typography.body1.fontSize;
}

export default function Avatar(theme: Theme) {
  const rootStyles = (ownerState: AvatarProps) => {
    const defaultStyle = {
      border: `solid 1px ${theme.palette.grey[300]}`,
    };

    const sizeStyle = SIZES.map((size) => ({
      ...(!!size &&
        ownerState.size === size && {
          width: size,
          height: size,
          borderRadius: getBorderRadiusBySizeAndVariant(
            theme,
            ownerState.variant,
            size
          ),
          fontSize: getFontSizeBySize(theme, size),
        }),
      ...(!ownerState.size && {
        height: 'auto',
        width: 'auto',
      }),
    }));

    const colorStyle = COLORS.map((color) => ({
      ...(ownerState.color === 'default' && {
        color: theme.palette.text.secondary,
        backgroundColor: alpha(theme.palette.grey[500], 0.24),
      }),
      ...(ownerState.color === color && {
        color: theme.palette[color]?.contrastText,
        backgroundColor: theme.palette[color]?.main,
      }),
    }));

    return {
      ...defaultStyle,
      ...sizeStyle.reduce((acc, style) => ({ ...acc, ...style }), {}),
      ...colorStyle.reduce((acc, style) => ({ ...acc, ...style }), {}),
    };
  };
  return {
    MuiAvatar: {
      defaultProps: {
        slotProps: { img: { loading: 'lazy' as const } },
      },
      styleOverrides: {
        root: ({ ownerState }: { ownerState: AvatarProps }) =>
          rootStyles(ownerState),
      },
    },
    MuiAvatarGroup: {
      defaultProps: {
        max: 4,
      },
      styleOverrides: {
        root: ({ ownerState }: { ownerState: AvatarGroupProps }) => ({
          justifyContent: 'flex-end',
          ...(ownerState.variant === 'compact' && {
            width: 40,
            height: 40,
            position: 'relative',
            [`& .${avatarGroupClasses.avatar}`]: {
              margin: 0,
              width: 28,
              height: 28,
              position: 'absolute',
              '&:first-of-type': {
                left: 0,
                bottom: 0,
                zIndex: 9,
              },
              '&:last-of-type': {
                top: 0,
                right: 0,
              },
            },
          }),
        }),
        avatar: {
          fontSize: 16,
          fontWeight: theme.typography.fontWeightSemiBold,
          '&:first-of-type': {
            aspectRatio: '1/1',
            fontSize: 12,
            color: theme.palette.primary[700],
            backgroundColor: theme.palette.primary[50],
          },
        },
      },
    },
  };
}
